/**
 * Parcel lookup — address or Regrid URL in, resolved deed out.
 *
 * Flow: type an address -> /search.json returns candidate parcels with their `path` ->
 * <path>.json returns geometry + assessor fields -> parcel.js resolves that polygon to the
 * RMA grid(s) and county it occupies -> the tab selects that grid.
 *
 * A pasted Regrid URL skips the search step. Pasted JSON skips the network entirely, which is
 * the fallback when CORS blocks the fetch (see regrid.js) — it always works, because the user
 * can open the .json in a tab and copy it.
 *
 * Chrome follows the Scoring Model tab, per §9.
 */

import React, { useEffect, useRef, useState } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { C } from "./data";
import { parseParcel } from "./parcel";
import { searchParcels, fetchParcel, looksLikePath, RegridError } from "./regrid";

const btn = (active, disabled) => ({
  padding: "5px 10px",
  fontSize: 10,
  fontWeight: 700,
  fontFamily: "'Inter',sans-serif",
  border: `1px solid ${active ? C.navy : C.border}`,
  background: active ? C.navy : C.white,
  color: disabled ? "#c3ccd7" : active ? C.white : C.navy,
  cursor: disabled ? "not-allowed" : "pointer",
  borderRadius: 3,
  whiteSpace: "nowrap",
});

const input = {
  flex: 1,
  minWidth: 0,
  padding: "6px 9px",
  fontSize: 11,
  fontFamily: "'Inter',sans-serif",
  border: `1px solid ${C.border}`,
  borderRadius: 3,
  color: C.charcoal,
  outline: "none",
};

const Row = ({ k, v }) => (
  <tr>
    <td style={{ padding: "3px 0", color: C.muted }}>{k}</td>
    <td
      style={{
        padding: "3px 0",
        textAlign: "right",
        fontWeight: 600,
        color: C.charcoal,
      }}
    >
      {v}
    </td>
  </tr>
);

export default function ParcelLookup({ parcel, onParcel, onClear, gridLabel }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [paste, setPaste] = useState(false);
  const [raw, setRaw] = useState("");
  const seq = useRef(0);

  useEffect(() => {
    if (parcel) {
      setHits(null);
      setPaste(false);
      setRaw("");
    }
  }, [parcel]);

  const fail = (e) => {
    setErr(
      e instanceof RegridError
        ? e.message
        : e?.message || "Could not read that parcel.",
    );
    // A blocked fetch is not recoverable by retrying, so surface the always-available route
    // rather than leaving the user to guess.
    if (e instanceof RegridError && e.kind === "cors") setPaste(true);
  };

  const load = async (path) => {
    const n = ++seq.current;
    setBusy(true);
    setErr(null);
    try {
      const json = await fetchParcel(path);
      if (n !== seq.current) return;
      const p = parseParcel(json);
      if (!p) throw new Error("That parcel has no usable geometry.");
      onParcel(p);
    } catch (e) {
      if (n === seq.current) fail(e);
    } finally {
      if (n === seq.current) setBusy(false);
    }
  };

  const go = async () => {
    const s = q.trim();
    if (!s) return;
    if (looksLikePath(s)) return load(s);
    const n = ++seq.current;
    setBusy(true);
    setErr(null);
    setHits(null);
    try {
      const r = await searchParcels(s);
      if (n !== seq.current) return;
      if (!r.length) setErr("No parcels matched that address.");
      else if (r.length === 1) return load(r[0].path);
      else setHits(r.slice(0, 8));
    } catch (e) {
      if (n === seq.current) fail(e);
    } finally {
      if (n === seq.current) setBusy(false);
    }
  };

  const applyPaste = () => {
    const p = parseParcel(raw);
    if (!p) {
      setErr("That is not a parcel payload — expected Regrid JSON or GeoJSON.");
      return;
    }
    setErr(null);
    onParcel(p);
  };

  return (
    <Card
      sx={{
        border: `1px solid ${C.border}`,
        borderTop: `3px solid ${parcel ? "#d92d20" : C.border}`,
        boxShadow: "none",
        borderRadius: 1,
        mb: 2,
      }}
    >
      <CardContent sx={{ pb: "14px !important" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 1,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.09em",
              color: C.navy,
              textTransform: "uppercase",
            }}
          >
            Parcel Lookup
          </Typography>
          <Box sx={{ flex: 1 }} />
          {parcel && (
            <button onClick={onClear} style={btn(false, false)}>
              Clear
            </button>
          )}
        </Box>

        {!parcel && (
          <>
            <Box sx={{ display: "flex", gap: 0.75, mb: 0.75 }}>
              <input
                style={input}
                value={q}
                placeholder="Address, or a Regrid parcel URL"
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && go()}
              />
              <button onClick={go} disabled={busy} style={btn(true, busy)}>
                {busy ? "Looking…" : "Find"}
              </button>
            </Box>
            <Typography sx={{ fontSize: 9.5, color: C.muted, mb: 1 }}>
              Resolves the deed to its RMA grid and county, draws it on the map,
              and selects that grid.
            </Typography>
          </>
        )}

        {hits && (
          <Box sx={{ mb: 1 }}>
            {hits.map((h) => (
              <Box
                key={h.path}
                onClick={() => load(h.path)}
                sx={{
                  p: 0.75,
                  borderBottom: `1px solid ${C.border}`,
                  cursor: "pointer",
                  "&:hover": { background: C.rowAlt },
                }}
              >
                <Typography
                  sx={{ fontSize: 11, fontWeight: 600, color: C.charcoal }}
                >
                  {h.headline}
                </Typography>
                <Typography sx={{ fontSize: 9.5, color: C.muted }}>
                  {[h.context, h.parcelNumber].filter(Boolean).join(" · ")}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {err && (
          <Typography
            sx={{
              fontSize: 10,
              color: C.charcoal,
              lineHeight: 1.5,
              bgcolor: "#fdf4f4",
              border: "1px solid #f0d5d5",
              borderRadius: 1,
              p: 0.75,
              mb: 1,
            }}
          >
            {err}
          </Typography>
        )}

        {!parcel && (
          <>
            {!paste ? (
              <button onClick={() => setPaste(true)} style={btn(false, false)}>
                Paste parcel JSON instead
              </button>
            ) : (
              <Box>
                <textarea
                  value={raw}
                  onChange={(e) => setRaw(e.target.value)}
                  placeholder="Paste the Regrid .json response, or any GeoJSON polygon"
                  style={{
                    ...input,
                    width: "100%",
                    height: 70,
                    resize: "vertical",
                    fontFamily: "monospace",
                    fontSize: 10,
                  }}
                />
                <button
                  onClick={applyPaste}
                  disabled={!raw.trim()}
                  style={{ ...btn(true, !raw.trim()), marginTop: 6 }}
                >
                  Resolve parcel
                </button>
              </Box>
            )}
          </>
        )}

        {parcel && (
          <>
            <Typography
              sx={{ fontSize: 13, fontWeight: 800, color: C.navy, mb: 0.25 }}
            >
              {parcel.label || parcel.parcelNumber || "Parcel"}
            </Typography>
            <Typography sx={{ fontSize: 10, color: C.muted, mb: 1 }}>
              {[
                parcel.countyName,
                parcel.state,
                parcel.zip,
                parcel.parcelNumber,
              ]
                .filter(Boolean)
                .join(" · ") || `County FIPS ${parcel.geoid || "—"}`}
            </Typography>

            <Box
              component="table"
              sx={{ width: "100%", fontSize: 10.5, borderCollapse: "collapse" }}
            >
              <tbody>
                <Row
                  k="Acreage"
                  v={`${parcel.acres.toLocaleString(undefined, {
                    maximumFractionDigits: 1,
                  })} ac${parcel.acresSource === "computed" ? " (from polygon)" : ""}`}
                />
                <Row
                  k="RMA grid"
                  v={
                    parcel.spansGridLine
                      ? `${parcel.grids.join(" · ")} — spans ${parcel.grids.length}`
                      : String(parcel.primary)
                  }
                />
                {parcel.spansGridLine && (
                  <Row
                    k="Acreage by grid"
                    v={Object.entries(parcel.shares)
                      .sort((a, b) => b[1] - a[1])
                      .map(([g, s]) => `${g} ${Math.round(s * 100)}%`)
                      .join(" · ")}
                  />
                )}
                <Row k="County FIPS" v={parcel.geoid || "—"} />
                {parcel.landValue != null && (
                  <Row
                    k="Assessed land value"
                    v={`$${parcel.landValue.toLocaleString()}`}
                  />
                )}
                {parcel.landValue != null && parcel.acres > 0 && (
                  <Row
                    k="Land value per acre"
                    v={`$${Math.round(parcel.landValue / parcel.acres).toLocaleString()}/ac`}
                  />
                )}
                {(parcel.useDesc || parcel.useCode) && (
                  <Row
                    k="Use"
                    v={parcel.useDesc || `code ${parcel.useCode}`}
                  />
                )}
                {parcel.owner && <Row k="Owner" v={parcel.owner} />}
              </tbody>
            </Box>

            {parcel.spansGridLine ? (
              <Box
                sx={{
                  mt: 1,
                  bgcolor: "#f2f8f4",
                  border: "1px solid #cfe6d8",
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: 10.5, color: C.charcoal, lineHeight: 1.5 }}
                >
                  <strong>Grid election available.</strong> This deed spans{" "}
                  {parcel.grids.length} grids, so all contiguous acres may be
                  elected into whichever one prices best.
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: 9.5, color: C.muted, mt: 1 }}>
                Sits entirely within one grid — no election available.
              </Typography>
            )}

            {gridLabel && (
              <Typography sx={{ fontSize: 9.5, color: C.muted, mt: 0.75 }}>
                Scorecard below is showing {gridLabel}.
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
